// Website pixels: the pure model behind the pixels screen, its drawer and its
// modals. Domain normalization, the draft validation that mirrors the server's
// `Pixel::validate`, the diff-only PATCH body, the shared defaults and the
// organization-defaults field rules. No React here.

import type {
  FiringConfig,
  InternalField,
  Pixel,
  PixelActivityRow,
  PixelApiValue,
  PixelConversions,
  PixelCreate,
  PixelDefaultsUpsert,
  PixelExtraction,
  PixelPii,
  PixelStatus,
  WebEventType,
} from '@/api/types';
import type { RequiredIssue } from '@/components/required-field';

export const PIXEL_EVENTS: readonly WebEventType[] = ['Pageview', 'Purchase'];

/**
 * The drawer's editable copy of a pixel: the API row plus the transient
 * provider/funnel enablement flags. The `_` keys never reach the server.
 */
export type PixelDraft = PixelApiValue & {
  _metaOn?: boolean;
  _ttdOn?: boolean;
  _funnelOn?: boolean;
};

/** One pixel's activity rows keyed by event type (`Pageview` / `Purchase`). */
export type PixelActivityByEvent = Record<string, PixelActivityRow>;

/**
 * `POST /pixels` body as the New-pixel modal sends it. `extraction`, `pii` and
 * `firing` are `#[serde(default)]` on `PixelCreate`, so they are left out.
 */
export type NewPixelBody = Omit<PixelCreate, 'extraction' | 'pii' | 'firing'>;

/** `PATCH /pixels/{id}` body (`PixelPatchRequest`): only what changed. */
export interface PixelPatch {
  tenant_id?: string;
  description?: string;
  status?: PixelStatus;
  deployed_at?: string;
  owner_email?: string;
  domains?: string[];
  meta_pixel_id?: string | null;
  ttd_adv_id?: string | null;
  ttd_ct_id?: string | null;
  ttd_ct_id_pageview?: string | null;
  default_venue_id?: string | null;
  default_production_id?: string | null;
  extraction?: PixelExtraction;
  pii?: PixelPii;
  firing?: FiringConfig;
  conversions?: PixelConversions;
}

// Normalize a user-typed domain string down to host only.
// Examples:
//   "https://us.test.com"        -> "test.com"
//   "www.test.com"               -> "test.com"
//   "test.com"                   -> "test.com"
//   "https://shubert.com/path?x" -> "shubert.com"
// Handles a small set of multi-part TLDs (.co.uk, .com.au, .co.jp …) so we
// don't accidentally strip past the registrable domain.
export function normalizeDomain(input: string | null | undefined): string {
  let s = (input || '').trim();
  if (!s) return '';
  s = s.replace(/^[a-z][a-z0-9+.\-]*:\/\//i, '');     // strip scheme
  s = s.replace(/^[^/@]*@/, '');                       // strip userinfo
  // `split` always yields at least one element; `?? ''` only tells the compiler.
  s = (((s.split('/')[0] ?? '').split('?')[0] ?? '').split('#')[0] ?? ''); // strip path/query/fragment
  s = s.split(':')[0] ?? '';                           // strip port
  s = s.toLowerCase();
  const parts = s.split('.').filter(Boolean);
  if (parts.length <= 2) return parts.join('.');
  const last = parts[parts.length - 1] ?? '';
  const second = parts[parts.length - 2] ?? '';
  const multi = new Set(['co','com','org','gov','ac','net','edu']);
  if (last.length === 2 && multi.has(second)) {
    return parts.slice(-3).join('.');
  }
  return parts.slice(-2).join('.');
}

// The registry stores domains in wire form (`https://host` — the shared-crate
// validator rejects bare hosts and anything with a path). Normalize whatever
// the user typed down to the host, then re-wrap.
export function toWireDomain(input: string | null | undefined): string {
  const host = normalizeDomain(input);
  return host ? `https://${host}` : '';
}

export function isValidPixelPath(path: string | null | undefined): boolean {
  const text = String(path ?? '');
  if (!text) return false;
  return text.split('.').every((part) => {
    const firstBracket = part.indexOf('[');
    const key = firstBracket >= 0 ? part.slice(0, firstBracket) : part;
    if (!key) return false;
    const suffix = firstBracket >= 0 ? part.slice(firstBracket) : '';
    return suffix === '' || /^(?:\[\d+\])+$/.test(suffix);
  });
}

export function isValidEncoreId(value: string, prefix: string): boolean {
  if (!value) return true;
  const uuid = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
  return new RegExp(`^(?:${uuid}|${prefix}/${uuid})$`).test(value);
}

// Defaults mirroring the shared Rust `Default` impls (inert-but-working).
export const DEFAULT_EXTRACTION: PixelExtraction = { auto_harvest: true, rules: [], pii_rules: [] };
export const DEFAULT_PII: PixelPii = { enabled: false, advanced_matching: false, resolve_identity: false, consent: 'always', consent_var: null };
export const DEFAULT_FIRING: FiringConfig = { meta: { purchase: true, pageview: true }, ttd: { purchase: true, pageview: true } };

/** The fields `pixelDraftRequiredIssues` reads. */
export type PixelDraftFields = Pick<
  PixelDraft,
  | 'owner_email'
  | 'tenant_id'
  | 'deployed_at'
  | 'domains'
  | 'meta_pixel_id'
  | 'ttd_adv_id'
  | 'ttd_ct_id'
  | 'ttd_ct_id_pageview'
  | 'default_venue_id'
  | 'default_production_id'
  | 'conversions'
  | 'extraction'
  | 'pii'
>;

export function pixelDraftRequiredIssues(draft: PixelDraftFields | null | undefined): RequiredIssue[] {
  if (!draft) return [];
  const issues: RequiredIssue[] = [];
  const owner = String(draft.owner_email ?? '').trim();
  const tenant = String(draft.tenant_id ?? '').trim();
  const domains = draft.domains || [];
  const meta = String(draft.meta_pixel_id ?? '').trim();
  const adv = String(draft.ttd_adv_id ?? '').trim();
  const conversion = String(draft.ttd_ct_id ?? '').trim();
  const pageview = String(draft.ttd_ct_id_pageview ?? '').trim();

  if (!/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(owner)) {
    issues.push({ key: 'pixel-owner', message: 'Enter a valid owner email.' });
  }
  if (!/^[a-z0-9_-]{1,64}$/.test(tenant)) {
    issues.push({
      key: 'pixel-tenant',
      message: 'Tenant must be a 1–64 character lowercase slug.',
    });
  }
  if (!String(draft.deployed_at ?? '').trim()) {
    issues.push({ key: 'pixel-deployed-at', message: 'Select the deployment date.' });
  }
  if (domains.length === 0) {
    issues.push({ key: 'pixel-domains', message: 'Add at least one install domain.' });
  } else if (domains.some((domain) => {
    try {
      const parsed = new URL(domain);
      return !['http:', 'https:'].includes(parsed.protocol)
        || !parsed.hostname
        || (parsed.pathname !== '' && parsed.pathname !== '/')
        || parsed.search
        || parsed.hash;
    } catch {
      return true;
    }
  })) {
    issues.push({
      key: 'pixel-domains',
      message: 'Every domain must be an HTTP(S) origin without a path, query, or fragment.',
    });
  }

  if (meta && !/^\d{10,20}$/.test(meta)) {
    issues.push({ key: 'pixel-meta-id', message: 'Meta Pixel ID must contain 10–20 digits.' });
  }
  if ((conversion || pageview) && !adv) {
    issues.push({ key: 'pixel-ttd-advertiser', message: 'TTD Advertiser ID is required when a conversion tag is set.' });
  }
  if (adv && !conversion && !pageview) {
    issues.push({ key: 'pixel-ttd-conversion', message: 'Add at least one TTD conversion tag ID.' });
  }
  if (conversion && !conversion.startsWith('0:')) {
    issues.push({ key: 'pixel-ttd-conversion', message: 'TTD conversion tag IDs must start with “0:”.' });
  }
  if (pageview && !pageview.startsWith('0:')) {
    issues.push({ key: 'pixel-ttd-pageview', message: 'TTD pageview tag IDs must start with “0:”.' });
  }
  if (!meta && !(adv && (conversion || pageview))) {
    issues.push({ key: 'pixel-platform', message: 'Enable and configure at least one provider.' });
  }
  if (!isValidEncoreId(String(draft.default_venue_id ?? '').trim(), 'Venue')) {
    issues.push({ key: 'pixel-default-venue', message: 'Select or enter a valid EncorePro venue ID.' });
  }
  if (!isValidEncoreId(String(draft.default_production_id ?? '').trim(), 'Production')) {
    issues.push({ key: 'pixel-default-production', message: 'Select a valid EncorePro production.' });
  }
  const testCode = String(draft.conversions?.test_event_code ?? '');
  if (testCode && testCode.length > 32) {
    issues.push({ key: 'pixel-test-event-code', message: 'Meta test event code must be 32 characters or fewer.' });
  }

  const extraction = draft.extraction || DEFAULT_EXTRACTION;
  const mappingRules = extraction.rules || [];
  const piiRules = extraction.pii_rules || [];
  mappingRules.forEach((rule, index) => {
    if (!isValidPixelPath(rule.source)) {
      issues.push({
        key: `pixel-mapping-rule-${index}`,
        message: 'Enter a valid dataLayer path such as ecommerce.value.',
      });
    }
  });
  piiRules.forEach((rule, index) => {
    if (!isValidPixelPath(rule.source)) {
      issues.push({
        key: `pixel-pii-rule-${index}`,
        message: 'Enter a valid PII dataLayer path such as customer.email.',
      });
    }
  });
  for (const eventType of ['Purchase', 'Pageview']) {
    const seen = new Map<InternalField, number>();
    mappingRules.forEach((rule, index) => {
      if (rule.event_type && rule.event_type !== eventType) return;
      const target = rule.target === 'show' ? 'production' : rule.target;
      if (seen.has(target)) {
        issues.push({
          key: `pixel-mapping-rule-${index}`,
          message: `${target || 'This target'} is mapped more than once for ${eventType}.`,
        });
      } else {
        seen.set(target, index);
      }
    });
  }
  const pii = draft.pii || DEFAULT_PII;
  if (
    (pii.consent === 'opt_in' || pii.consent === 'opt_out')
    && !String(pii.consent_var ?? '').trim()
  ) {
    issues.push({
      key: 'pixel-consent-var',
      message: 'Enter the dataLayer path that controls consent.',
    });
  }

  return issues;
}

// Chips and filter labels show just the host.
export function domainLabel(d: string | null | undefined): string {
  return String(d || '').replace(/^[a-z][a-z0-9+.\-]*:\/\//i, '').replace(/\/$/, '');
}

export function pixelCoverage(p: Pick<Pixel, 'meta_pixel_id' | 'ttd_adv_id' | 'ttd_ct_id'>): { meta: boolean; ttd: boolean } {
  return {
    meta: !!p.meta_pixel_id,
    ttd: !!(p.ttd_adv_id || p.ttd_ct_id),
  };
}

// The nullable id/provider columns a PATCH collapses '' to null for.
type NullableIdField =
  | 'meta_pixel_id' | 'ttd_adv_id' | 'ttd_ct_id' | 'ttd_ct_id_pageview'
  | 'default_venue_id' | 'default_production_id';
const NULLABLE_ID_FIELDS: readonly NullableIdField[] = [
  'meta_pixel_id', 'ttd_adv_id', 'ttd_ct_id', 'ttd_ct_id_pageview',
  'default_venue_id', 'default_production_id',
];

// Diff-only PATCH body: only fields the operator actually changed, with ''
// collapsed to null for the nullable id/provider columns (PixelPatch uses
// double-Option semantics server-side — an absent key means "leave as is").
export function buildPixelPatch(before: PixelApiValue, draft: PixelDraft): PixelPatch {
  const norm = (v: string | null | undefined): string | null => (v === '' || v === undefined ? null : v);
  const patch: PixelPatch = {};
  if ((draft.tenant_id || '') !== (before.tenant_id || '')) patch.tenant_id = draft.tenant_id || '';
  if ((draft.description || '') !== (before.description || '')) patch.description = draft.description || '';
  if ((draft.deployed_at || '') !== (before.deployed_at || '')) patch.deployed_at = draft.deployed_at;
  if ((draft.owner_email || '') !== (before.owner_email || '')) patch.owner_email = draft.owner_email || '';
  const beforeDomains = before.domains || [];
  const draftDomains = draft.domains || [];
  if (JSON.stringify(beforeDomains) !== JSON.stringify(draftDomains)) patch.domains = draftDomains;
  for (const f of NULLABLE_ID_FIELDS) {
    if (norm(draft[f]) !== norm(before[f])) patch[f] = norm(draft[f]);
  }
  // Universal-pixel config objects — whole-object replace when changed.
  // `conversions` (server conversion toggles) rides the same idiom; the drawer
  // materializes a default object when the row predates the column, so a
  // toggle away from all-off always diffs against the stored null.
  if (JSON.stringify(before.extraction ?? null) !== JSON.stringify(draft.extraction ?? null)) {
    if (draft.extraction != null) patch.extraction = draft.extraction;
  }
  if (JSON.stringify(before.pii ?? null) !== JSON.stringify(draft.pii ?? null)) {
    if (draft.pii != null) patch.pii = draft.pii;
  }
  if (JSON.stringify(before.firing ?? null) !== JSON.stringify(draft.firing ?? null)) {
    if (draft.firing != null) patch.firing = draft.firing;
  }
  if (JSON.stringify(before.conversions ?? null) !== JSON.stringify(draft.conversions ?? null)) {
    if (draft.conversions != null) patch.conversions = draft.conversions;
  }
  return patch;
}

// ── Organization defaults ────────────────────────────────────────────────────
// Prefill-only settings: they seed the New-pixel form and the provider toggles
// and are NEVER a live fallback, so saving here cannot change how an already
// registered pixel behaves. The copy says so explicitly — "organization default"
// reads like a runtime fallback otherwise.
//
// Validation deliberately mirrors the SERVER's looser `PixelDefaultsUpsert`
// rules, not `Pixel::validate`: any subset is legal (Meta alone, TTD alone, or
// nothing), and an incomplete TTD triple warns rather than blocks.
export interface DefaultsField {
  key: keyof PixelDefaultsUpsert;
  label: string;
  placeholder: string;
  hint: string;
}

export const DEFAULTS_FIELDS: readonly DefaultsField[] = [
  { key: 'tenant_id', label: 'Tenant', placeholder: 'tenant slug',
    hint: 'Prefills the Tenant field on new pixels.' },
  { key: 'meta_pixel_id', label: 'Meta pixel ID', placeholder: '10–20 digits',
    hint: 'Seeds the Meta ID when the Meta provider is switched on.' },
  { key: 'ttd_adv_id', label: 'TTD advertiser ID', placeholder: 'advertiser id',
    hint: 'Seeds the advertiser when the TTD provider is switched on.' },
  { key: 'ttd_ct_id', label: 'TTD conversion tag', placeholder: '0:…',
    hint: 'Must start with 0:' },
  { key: 'ttd_ct_id_pageview', label: 'TTD pageview tag', placeholder: '0:…',
    hint: 'Must start with 0:' },
];

export function defaultsFieldError(key: string, value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (!v) return null; // blank = cleared; always legal
  if (key === 'tenant_id') return /^[a-z0-9_-]{1,64}$/.test(v) ? null : 'Lowercase letters, digits, _ and - only (max 64).';
  if (key === 'meta_pixel_id') return /^\d{10,20}$/.test(v) ? null : 'Meta pixel ID must be 10–20 digits.';
  if (key === 'ttd_ct_id' || key === 'ttd_ct_id_pageview') return v.startsWith('0:') ? null : 'TTD tag IDs start with 0:';
  return null;
}

/** `PUT /pixels/defaults` body from the drawer's text fields: blank → null. */
export function defaultsUpsertBody(draft: Readonly<Record<string, string>>): PixelDefaultsUpsert {
  const value = (key: keyof PixelDefaultsUpsert) => (draft[key] ?? '').trim() || null;
  return {
    tenant_id: value('tenant_id'),
    meta_pixel_id: value('meta_pixel_id'),
    ttd_adv_id: value('ttd_adv_id'),
    ttd_ct_id: value('ttd_ct_id'),
    ttd_ct_id_pageview: value('ttd_ct_id_pageview'),
  };
}
